import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarIcon, CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { format } from "date-fns";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { SERVICES, TRUST } from "../lib/content";
import { createBooking } from "../lib/api";
import SectionTitle from "../components/site/SectionTitle";

const TIME_SLOTS = ["09:00 AM", "10:30 AM", "12:00 PM", "02:00 PM", "04:00 PM", "06:00 PM"];

const buildWaMessage = ({ name, service, date, time, msg }) => {
  const lines = [
    "Assalamu Alaikum, I would like to book a session with Hazrat Sufi Sarkar.",
    name && `Name: ${name}`,
    service && `Service: ${service}`,
    date && `Preferred date: ${date}`,
    time && `Preferred time: ${time}`,
    msg && `Note: ${msg}`,
  ].filter(Boolean);
  return encodeURIComponent(lines.join("\n"));
};

export default function BookOnline() {
  useEffect(() => { document.title = "Book Online — Hazrat Sufi Sarkar"; }, []);
  const location = useLocation();
  const prefilled = location.state?.service || "";

  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: prefilled, message: "",
  });
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const waHref = `https://wa.me/${TRUST.whatsapp}?text=${buildWaMessage({
    name: form.name,
    service: form.service,
    date: date ? format(date, "PPP") : "",
    time,
    msg: form.message,
  })}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !date) {
      toast.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      await createBooking({
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        message: form.message,
      });
      setSubmitted(true);
      toast.success("Thanks for submitting! The trust will contact you soon.");
    } catch (err) {
      toast.error("Could not submit. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="book-page-success" className="min-h-[70vh] pattern-bg flex items-center">
        <div className="container-page max-w-2xl text-center">
          <CheckCircle2 className="text-gold mx-auto" size={64} />
          <h1 className="font-serif text-4xl md:text-5xl text-emerald mt-6">Thanks for submitting!</h1>
          <p className="text-charcoal-muted mt-5 text-lg">
            Your request has been received. The trust will reach out within 24–48 hours, Insha'Allah.
          </p>
          <a href={`https://wa.me/${TRUST.whatsapp}`} target="_blank" rel="noreferrer" data-testid="success-wa-btn" className="btn-gold mt-8 inline-flex">
            <MessageCircle size={16} className="mr-2" /> Continue on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page">
      <section className="pattern-emerald text-ivory pt-32 pb-20" data-testid="book-hero">
        <div className="container-page text-center max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.32em] text-gold">Book Online</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">Schedule a Session</h1>
          <p className="text-ivory/80 mt-5">
            The fastest way to reach the trust is via WhatsApp — your message goes directly to Sarkar's team.
          </p>
          <a
            href={`https://wa.me/${TRUST.whatsapp}?text=${encodeURIComponent("Assalamu Alaikum, I would like to book a session with Hazrat Sufi Sarkar.")}`}
            target="_blank"
            rel="noreferrer"
            data-testid="wa-primary-btn"
            className="mt-10 inline-flex items-center gap-3 px-10 py-5 rounded-full bg-[#25D366] text-white font-semibold hover:bg-[#1ebe5a] transition-all duration-300 shadow-lg shadow-emerald-deep/40 hover:scale-[1.02]"
          >
            <MessageCircle size={22} />
            <span>Book on WhatsApp · {TRUST.whatsappDisplay}</span>
          </a>
          <div className="mt-6 flex items-center justify-center gap-2 text-ivory/60 text-sm">
            <Phone size={14} className="text-gold" />
            <span>Or call us at <a href={`tel:+${TRUST.whatsapp}`} className="text-gold hover:underline">{TRUST.whatsappDisplay}</a></span>
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="book-form-section">
        <div className="container-page max-w-3xl">
          <SectionTitle eyebrow="Or fill a request below" title="A gentle conversation begins here" subtitle="If you prefer not to use WhatsApp, share your details and the trust will contact you." />
          <form onSubmit={submit} className="card-soft p-8 md:p-12 space-y-6" data-testid="booking-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Full Name *</Label>
                <Input id="name" data-testid="booking-name" value={form.name} onChange={onChange("name")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" placeholder="Your full name" />
              </div>
              <div>
                <Label htmlFor="email" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Email *</Label>
                <Input id="email" type="email" data-testid="booking-email" value={form.email} onChange={onChange("email")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="phone" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Phone *</Label>
                <Input id="phone" data-testid="booking-phone" value={form.phone} onChange={onChange("phone")} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" placeholder="+91 ..." />
              </div>
              <div>
                <Label className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Service *</Label>
                <Select value={form.service} onValueChange={(v) => setForm(p => ({ ...p, service: v }))}>
                  <SelectTrigger data-testid="booking-service" className="mt-2 bg-transparent border-gold/40 focus:ring-gold">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map(s => (
                      <SelectItem key={s.key} value={s.title}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Preferred Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="booking-date-trigger"
                      className="mt-2 w-full inline-flex items-center justify-between border border-gold/40 rounded-md px-3 py-2 text-left text-sm hover:bg-gold/5"
                    >
                      <span className={date ? "text-charcoal" : "text-charcoal-muted"}>
                        {date ? format(date, "PPP") : "Pick a date"}
                      </span>
                      <CalendarIcon size={16} className="text-gold" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-ivory" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Preferred Time</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="booking-time" className="mt-2 bg-transparent border-gold/40 focus:ring-gold">
                    <SelectValue placeholder="Choose a time" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="message" className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Your Message</Label>
              <Textarea id="message" data-testid="booking-message" value={form.message} onChange={onChange("message")} rows={5} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" placeholder="Briefly share what's on your heart (optional)" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="wa-form-btn" className="inline-flex items-center gap-2 text-[#1ebe5a] hover:text-emerald font-semibold text-sm">
                <MessageCircle size={16} /> Send via WhatsApp instead
              </a>
              <button type="submit" data-testid="booking-submit" disabled={loading} className="btn-gold disabled:opacity-60">
                {loading ? "Submitting..." : "Submit Request"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
